import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Crown, X } from 'lucide-react';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import { useFeatures } from '../context/FeatureContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useFilm } from '../context/FilmContext';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { apiGet, qk } from '../hooks/queries';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import DirectorsCommandView from '../components/DirectorsCommandView';
import { API_BASE } from '../config/api';

const API = API_BASE;

/**
 * Home — `/`
 * Single-workspace users are auto-redirected into their workspace.
 * Multi-workspace users see the "Director's Command" cross-workspace view.
 */
const Home = () => {
  const { user } = useAuth();
  const { refreshFeatures } = useFeatures();
  const {
    workspaces,
    loading: workspacesLoading,
    deleteWorkspace,
  } = useWorkspace();
  const { fetchFilms: refreshFilmContext } = useFilm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Films aggregated from per-workspace endpoints (gives us scene/shot counts).
  // [K] TanStack Query: one cached query per workspace via useQueries — shared
  // with WorkspaceHome's cache and dedup'd across navigations.
  const filmQueries = useQueries({
    queries: (workspaces || []).map((ws) => ({
      queryKey: qk.workspaceFilms(ws.id),
      queryFn: ({ signal }) => apiGet(`/workspaces/${ws.id}/films`, signal),
      enabled: !!ws.id,
    })),
  });
  const filmsLoading = workspacesLoading || filmQueries.some((q) => q.isLoading);
  const filmsSignal = filmQueries.map((q) => `${q.dataUpdatedAt}`).join('|');
  const films = useMemo(() => {
    const merged = [];
    (workspaces || []).forEach((ws, i) => {
      const data = filmQueries[i]?.data || [];
      for (const f of data) {
        merged.push({ ...f, workspace_id: f.workspace_id || ws.id, workspace_name: ws.name });
      }
    });
    merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return merged;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces, filmsSignal]);

  // Create-film dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [creating, setCreating] = useState(false);

  // Pending workspace purchase banner
  const [pendingPurchase, setPendingPurchase] = useState(null);
  const [pendingDismissed, setPendingDismissed] = useState(false);

  // Workspace delete confirmation
  const [pendingDeleteWs, setPendingDeleteWs] = useState(null);
  const [deletingWs, setDeletingWs] = useState(false);

  /* ----- effects ----- */

  useEffect(() => {
    const controller = new AbortController();
    const check = async () => {
      try {
        const r = await axios.get(`${API}/razorpay/workspace/pending-purchase`, { headers: getAuthHeaders(), signal: controller.signal });
        if (controller.signal.aborted) return;
        if (r.data?.has_pending && r.data?.purchase) {
          setPendingPurchase(r.data.purchase);
        }
      } catch {
        // not critical
      }
    };
    check();
    return () => controller.abort();
  }, []);

  // Auto-redirect for single-workspace users
  useEffect(() => {
    if (!workspacesLoading && workspaces.length === 1) {
      navigate(`/workspaces/${workspaces[0].id}`, { replace: true });
    }
  }, [workspacesLoading, workspaces, navigate]);

  useEffect(() => {
    if (workspaces.length > 0 && !selectedWorkspace) {
      setSelectedWorkspace(workspaces[0].id);
    }
  }, [workspaces, selectedWorkspace]);

  /* ----- handlers ----- */

  const handleCreateFilm = useCallback(async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { title, description };
      if (selectedWorkspace && selectedWorkspace !== 'personal') {
        payload.workspace_id = selectedWorkspace;
      }
      const res = await axios.post(`${API}/films`, payload, { headers: getAuthHeaders() });
      setTitle('');
      setDescription('');
      setSelectedWorkspace(workspaces[0]?.id || '');
      setDialogOpen(false);
      // Refresh both: the cross-workspace context (for FilmSwitcher etc.)
      // and our local per-workspace films list (RQ cache).
      refreshFilmContext(true);
      queryClient.invalidateQueries({ queryKey: ['workspace'] });
      refreshFeatures();
      toast.success('Film created');
      // Script-first onboarding (Phase 1 Task 2): go straight to the film
      // dashboard with the script-upload dropzone auto-expanded.
      const created = res.data;
      if (created?.id) {
        const wsId = created.workspace_id
          || (selectedWorkspace && selectedWorkspace !== 'personal' ? selectedWorkspace : null);
        navigate(
          wsId
            ? `/workspaces/${wsId}/films/${created.id}?import=script`
            : `/films/${created.id}?import=script`
        );
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to create film';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  }, [title, description, selectedWorkspace, workspaces, queryClient, refreshFilmContext, refreshFeatures, navigate]);

  const handleDeleteWs = (ws) => setPendingDeleteWs(ws);
  const confirmDeleteWs = async () => {
    if (!pendingDeleteWs) return;
    setDeletingWs(true);
    try {
      await deleteWorkspace(pendingDeleteWs.id);
      toast.success(`Deleted ${pendingDeleteWs.name}`);
      setPendingDeleteWs(null);
    } catch (e) {
      toast.error(e?.message || 'Failed to delete workspace');
    } finally {
      setDeletingWs(false);
    }
  };

  // Sort workspaces: owned first, then by name
  const sortedWorkspaces = useMemo(() => {
    return [...workspaces].sort((a, b) => {
      const aOwn = (a.role === 'owner' || a.owner_id === user?.id) ? 0 : 1;
      const bOwn = (b.role === 'owner' || b.owner_id === user?.id) ? 0 : 1;
      if (aOwn !== bOwn) return aOwn - bOwn;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [workspaces, user?.id]);

  // While we wait for the auto-redirect on single-workspace users
  if (!workspacesLoading && workspaces.length === 1) {
    return (
      <div className="container mx-auto px-4 md:px-6 py-12 text-muted-foreground text-sm">
        Loading workspace…
      </div>
    );
  }

  /* ----- pending purchase banner (above hero) ----- */
  const purchaseBanner = pendingPurchase && !pendingDismissed ? (
    <div
      className="mb-8 p-4 rounded-2xl border border-warning/30 bg-warning/10"
      data-testid="pending-purchase-banner"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 rounded-xl bg-warning/20 text-warning shrink-0">
            <Crown className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-foreground truncate">
              Complete your {pendingPurchase.workspace_type === 'studio' ? 'Studio' : 'Pro'} workspace setup
            </p>
            <p className="text-sm text-muted-foreground truncate">
              Payment received. Finish setup to start using your new workspace.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            onClick={() => setPendingDismissed(true)}
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </Button>
          <Button asChild size="sm" className="rounded-xl">
            <Link to={`/workspace/create?purchase_id=${pendingPurchase.id}`}>
              Create Workspace
            </Link>
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="bg-background" data-testid="home-page">
      <DirectorsCommandView
        workspaces={sortedWorkspaces}
        films={films}
        currentUserId={user?.id}
        loading={workspacesLoading}
        filmsLoading={filmsLoading}
        showCreateFilmCta
        onCreateFilm={() => setDialogOpen(true)}
        onCreateWorkspace={() => navigate('/workspace/purchase')}
        onDeleteWorkspace={handleDeleteWs}
        beforeHero={purchaseBanner}
      />

      {/* Create film dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="create-film-dialog">
          <DialogHeader>
            <DialogTitle>Create New Film</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateFilm} className="space-y-4">
            {workspaces.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="workspace-select">Create under</Label>
                <select
                  id="workspace-select"
                  value={selectedWorkspace}
                  onChange={(e) => setSelectedWorkspace(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background"
                >
                  {workspaces.map((ws) => (
                    <option key={ws.id} value={ws.id}>{ws.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="title">Film Title</Label>
              <Input
                id="title"
                data-testid="film-title-input"
                placeholder="Enter film title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                data-testid="film-description-input"
                placeholder="Brief description of your film"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              data-testid="submit-film-button"
              disabled={creating}
            >
              {creating ? 'Creating...' : 'Create Film'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Workspace delete confirmation */}
      <AlertDialog open={!!pendingDeleteWs} onOpenChange={(open) => { if (!open) setPendingDeleteWs(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete workspace?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <span className="font-semibold">{pendingDeleteWs?.name}</span>{' '}
              and all of its films, scenes, and shots. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingWs}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteWs}
              disabled={deletingWs}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-workspace"
            >
              {deletingWs ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Home;
